/**
 * #931 — the "pending logout" marker.
 *
 * Signing out clears this device's copy of the session at once, but the
 * SERVER session — and the HttpOnly `refresh_token` / `palate_session`
 * cookies the browser still holds — only end when POST /v1/auth/logout
 * reaches the backend. Until it does, those cookies can mint a brand-new
 * session: the boot path's cookie refresh on /login would sign the previous
 * user straight back in (on a shared back-office PC, "Log out" did not log
 * out).
 *
 * The marker is written BEFORE local state is cleared and removed only when
 * the server confirms the session is gone. While it is present:
 *   - no cookie-based refresh runs in any tab (axios.config.ts
 *     refreshSessionDeduped);
 *   - boot re-attempts the server logout instead of refreshing (root.tsx);
 *   - pending-logout.ts retries in the background, on `online`, and on unload.
 *
 * localStorage, not sessionStorage: it must survive a reload, a closed tab
 * and a browser restart, and be visible to every tab — and sign-out itself
 * wipes sessionStorage.
 *
 * `accessToken` is the signed-out session's (short-lived) access token.
 * POST /v1/auth/logout accepts a Bearer access token OR the `palate_session`
 * cookie — never the refresh cookie — and on the cookie path it only revokes
 * the JWT session (and with it the refresh token) when the lapsed access token
 * rides along too (backend#1076, `revokeLapsedAccessToken`). The long-lived
 * refresh token is deliberately NOT kept.
 *
 * Leaf module: no app imports, so axios.config.ts can read it without an
 * import cycle.
 */
export const PENDING_LOGOUT_STORAGE_KEY = "palate-pending-logout";

export interface PendingLogout {
  /** Epoch ms the sign-out started. */
  since: number;
  /** The signed-out session's access token, when there was one. */
  accessToken: string | null;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Some privacy modes throw on access.
    return null;
  }
}

export function markPendingLogout(accessToken: string | null): void {
  const pending: PendingLogout = { since: Date.now(), accessToken };
  try {
    storage()?.setItem(PENDING_LOGOUT_STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Quota / privacy mode: the in-page retry still runs; only the
    // cross-reload guard is lost.
  }
}

export function readPendingLogout(): PendingLogout | null {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(PENDING_LOGOUT_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingLogout> | null;
    return {
      since: typeof parsed?.since === "number" ? parsed.since : 0,
      accessToken:
        typeof parsed?.accessToken === "string" ? parsed.accessToken : null,
    };
  } catch {
    // A corrupt marker still means "a sign-out has not been confirmed" —
    // fail closed (block the cookie refresh), just without a token to send.
    return { since: 0, accessToken: null };
  }
}

export function hasPendingLogout(): boolean {
  return readPendingLogout() !== null;
}

export function clearPendingLogout(): void {
  try {
    storage()?.removeItem(PENDING_LOGOUT_STORAGE_KEY);
  } catch {
    // Nothing else to do — see markPendingLogout.
  }
}
